import React from 'react';
import { Helmet } from 'react-helmet';

const bullet = {
  position: 'relative',
  paddingLeft: '22px',
  fontSize: '15px',
  color: '#374151',
  lineHeight: '1.7',
  marginBottom: '6px',
};

const bulletDot = {
  content: '""',
  position: 'absolute',
  left: 0,
  top: '12px',
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  backgroundColor: '#4F46E5',
};

const BulletItem = ({ children }) => (
  <li style={bullet}>
    <span style={bulletDot} aria-hidden="true" />
    {children}
  </li>
);

const SectionHeading = ({ children }) => (
  <h2
    style={{
      fontSize: '18px',
      fontWeight: 800,
      color: '#4F46E5',
      margin: '40px 0 12px',
      paddingBottom: '8px',
      borderBottom: '2px solid #EDE9FE',
    }}
  >
    {children}
  </h2>
);

const SubHeading = ({ children }) => (
  <h3
    style={{
      fontSize: '16px',
      fontWeight: 700,
      color: '#1E1B4B',
      margin: '20px 0 8px',
    }}
  >
    {children}
  </h3>
);

const Card = ({ title, children }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #EDE9FE',
      borderRadius: '16px',
      padding: '20px 24px',
      marginBottom: '16px',
    }}
  >
    {title && <strong style={{ color: '#1E1B4B' }}>{title}</strong>}
    {children}
  </div>
);

const Para = ({ children, style: extra }) => (
  <p style={{ marginBottom: '14px', fontSize: '15px', color: '#374151', lineHeight: '1.7', ...extra }}>
    {children}
  </p>
);

const Link = ({ href, children }) => (
  <a href={href} style={{ color: '#4F46E5', textDecoration: 'none' }}>
    {children}
  </a>
);

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-6YQYMBQQNY"
        />
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-6YQYMBQQNY');
          `}
        </script>

        <script>
          {`
            (function(w,d,t,r,u) {
              var f,n,i;
              w[u] = w[u] || [];
              f = function() {
                var o = {ti: "97123631", enableAutoSpaTracking: true};
                o.q = w[u];
                w[u] = new UET(o);
                w[u].push("pageLoad");
              },
              n = d.createElement(t);
              n.src = r;
              n.async = 1;
              n.onload = n.onreadystatechange = function() {
                var s = this.readyState;
                if(!s || s==="loaded" || s==="complete") {
                  f();
                  n.onload = n.onreadystatechange = null;
                }
              };
              i = d.getElementsByTagName(t)[0];
              i.parentNode.insertBefore(n,i);
            })(window,document,"script","//bat.bing.com/bat.js","uetq");

            window.uetq = window.uetq || [];
            window.uetq.push('event', '', {
              "revenue_value": 0,
              "currency": "USD"
            });
          `}
        </script>

        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta
          name="description"
          content="Aurora Prep LLC Terms of Use and Privacy Policy. Learn how we collect, use, and protect your personal information."
        />
        <meta
          name="keywords"
          content="consulting prep, consulting case prep, consulting interview prep, privacy policy, terms of use, data protection, Aurora Prep"
        />
        <title>Terms of Use &amp; Privacy Policy — Aurora Prep LLC</title>
      </Helmet>

      <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", background: '#F9FAFB', color: '#111827', lineHeight: '1.7', minHeight: '100vh' }}>
        {/* Header */}
        <header
          style={{
            background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
            color: '#fff',
            padding: '52px 24px 40px',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '28px', fontWeight: 800, letterSpacing: '-0.5px' }}>
            Terms of Use &amp; Privacy Policy
          </h1>
          <p style={{ marginTop: '8px', fontSize: '14px', opacity: 0.8 }}>
            Aurora Prep LLC
          </p>
        </header>

        {/* Container */}
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '48px 16px 80px' }}>
          {/* Highlight */}
          <div
            style={{
              background: '#EEF2FF',
              borderLeft: '4px solid #4F46E5',
              borderRadius: '0 12px 12px 0',
              padding: '14px 18px',
              marginBottom: '20px',
              fontSize: '15px',
              color: '#3730A3',
              lineHeight: '1.7',
            }}
          >
            Please read these Terms of Use and Privacy Policy carefully before
            accessing or using the Aurora Prep LLC website, platform, or consulting
            preparation training program. By accessing or using our Services, you
            agree to be bound by these Terms. If you do not agree, do not use the
            Services.
          </div>

          <Para>
            These Terms constitute a legally binding agreement between you
            (&ldquo;User,&rdquo; &ldquo;you,&rdquo; or &ldquo;your&rdquo;) and
            Aurora Prep LLC, a Delaware corporation (&ldquo;Aurora Prep,&rdquo;
            &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;).
          </Para>

          {/* ===== PART I ===== */}
          <h2
            style={{
              fontSize: '22px',
              fontWeight: 800,
              color: '#1E1B4B',
              margin: '48px 0 24px',
              paddingBottom: '12px',
              borderBottom: '3px solid #4F46E5',
            }}
          >
            Part I — Terms of Use
          </h2>

          {/* 1 */}
          <SectionHeading>1. Eligibility and Account Registration</SectionHeading>
          <Para>
            You must be at least 16 years of age to use the Services. By
            registering an account, you represent and warrant that all information
            you provide is accurate and complete. You are responsible for
            maintaining the confidentiality of your account credentials and for all
            activity that occurs under your account. Aurora Prep reserves the right
            to suspend or terminate accounts that violate these Terms.
          </Para>

          {/* 2 */}
          <SectionHeading>2. License and Permitted Use</SectionHeading>
          <Para>
            Subject to these Terms, Aurora Prep grants you a limited, non-exclusive,
            non-transferable, revocable license to access and use the Services for
            your personal, non-commercial educational preparation purposes. You may
            not:
          </Para>
          <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
            <BulletItem>Share, sublicense, resell, or distribute access to the Services to any third party</BulletItem>
            <BulletItem>Copy, reproduce, modify, or create derivative works from any course content, practice materials, or platform features</BulletItem>
            <BulletItem>Impersonate another user, person, or entity</BulletItem>
            <BulletItem>Use the Services for any unlawful purpose or in violation of these Terms</BulletItem>
          </ul>

          {/* 3 */}
          <SectionHeading>3. Institutional Sponsorship</SectionHeading>
          <Para>
            Aurora Prep may partner with universities, employers, or other
            organizations (&ldquo;Sponsoring Institutions&rdquo;) that purchase
            access on behalf of their students or employees. If your access is
            sponsored:
          </Para>
          <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
            <BulletItem>Your account is created directly by you, and you provide your own information to Aurora Prep</BulletItem>
            <BulletItem>
              Sponsoring Institutions have no role in the collection or processing
              of your personal data. Aurora Prep collects information only from you
              directly, and no academic records, institutional records, or
              enrollment information are shared with or received by Aurora Prep from
              any educational institution.
            </BulletItem>
          </ul>

          {/* 4 */}
          <SectionHeading>4. Payments and Subscriptions</SectionHeading>
          <Card>
            <Para style={{ marginTop: '8px' }}>
              Aurora Prep offers subscription-based access to the Services. All
              payments are processed by a third-party payment processor (currently
              Stripe). Aurora Prep does not store raw credit card or financial account
              data on its systems. By submitting payment information, you authorize
              Aurora Prep and its payment processors to charge the applicable fees.
            </Para>
            <Para>
              Subscription fees are billed in advance on a recurring basis. You may
              cancel your subscription at any time; cancellation takes effect at the
              end of the current billing period. Aurora Prep does not provide refunds
              for partial subscription periods unless required by applicable law.
            </Para>
          </Card>

          {/* 5 */}
          <SectionHeading>5. Acceptable Use and Academic Integrity</SectionHeading>
          <Para>
            The Services are designed to support genuine learning and preparation.
            You agree not to:
          </Para>
          <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
            <BulletItem>Submit work, assessments, or practice cases that misrepresent your authorship</BulletItem>
            <BulletItem>Use AI tools, third parties, or other resources to complete timed assessments in a manner that misrepresents your own performance</BulletItem>
            <BulletItem>Share platform content, practice questions, or assessments with others outside your authorized access</BulletItem>
            <BulletItem>Engage in any conduct that disrupts or harms the Services or other users</BulletItem>
          </ul>

          {/* 6 */}
          <SectionHeading>6. Intellectual Property</SectionHeading>
          <Para>
            All content on the Services, including but not limited to course
            materials, case frameworks, video content, practice questions, UI
            design, and platform software, is owned by or licensed to Aurora Prep
            and protected by applicable intellectual property laws. Nothing in these
            Terms grants you any ownership interest in Aurora Prep content. Feedback
            or suggestions you provide to Aurora Prep may be used by Aurora Prep
            without restriction or compensation.
          </Para>

          {/* 7 */}
          <SectionHeading>7. Third-Party Links and Services</SectionHeading>
          <Para>
            The Services may contain links to third-party websites or services.
            Aurora Prep does not endorse and is not responsible for the content,
            privacy practices, or availability of any third-party sites. Accessing
            third-party content is at your own risk.
          </Para>

          {/* 8 */}
          <SectionHeading>8. Disclaimers</SectionHeading>
          <Card>
            <Para style={{ marginTop: '8px', textTransform: 'uppercase', fontSize: '13px' }}>
              The Services are provided on an &ldquo;as is&rdquo; and &ldquo;as
              available&rdquo; basis without warranty of any kind, express or implied.
              Aurora Prep makes no warranty that the Services will meet your
              requirements, be uninterrupted, timely, secure, or error-free. Aurora
              Prep does not guarantee any specific career, interview, or employment
              outcome from use of the Services.
            </Para>
          </Card>

          {/* 9 */}
          <SectionHeading>9. Limitation of Liability</SectionHeading>
          <Card>
            <Para style={{ marginTop: '8px', textTransform: 'uppercase', fontSize: '13px' }}>
              To the fullest extent permitted by applicable law, Aurora Prep shall not
              be liable for any indirect, incidental, special, consequential, or
              punitive damages, including loss of profits, data, or goodwill, arising
              out of or in connection with your use of the Services, even if Aurora
              Prep has been advised of the possibility of such damages. Aurora
              Prep&rsquo;s total liability for any claim arising out of these Terms
              shall not exceed the amount you paid to Aurora Prep in the 12 months
              preceding the claim.
            </Para>
          </Card>

          {/* 10 */}
          <SectionHeading>10. Governing Law and Dispute Resolution</SectionHeading>
          <Para>
            These Terms are governed by and construed in accordance with the laws of
            the State of Delaware, without regard to conflict of law principles. Any
            dispute arising from or relating to these Terms or the Services shall be
            resolved by binding individual arbitration under the rules of the
            American Arbitration Association (AAA), except that either party may
            seek injunctive or other equitable relief in a court of competent
            jurisdiction. You waive any right to participate in a class action
            lawsuit or class-wide arbitration.
          </Para>

          {/* 11 */}
          <SectionHeading>11. Changes to Terms</SectionHeading>
          <Para>
            Aurora Prep reserves the right to modify these Terms at any time. We
            will notify you of material changes by posting the updated Terms on our
            website and updating the &ldquo;Last Updated&rdquo; date above.
            Continued use of the Services after changes are posted constitutes your
            acceptance of the revised Terms.
          </Para>

          {/* 12 */}
          <SectionHeading>12. Termination</SectionHeading>
          <Para>
            Aurora Prep may suspend or terminate your access to the Services at any
            time for violation of these Terms or for any other reason with
            reasonable notice. Upon termination, your license to use the Services
            ceases immediately. Sections 6, 8, 9, 10, and all relevant provisions
            of Part II survive termination.
          </Para>

          {/* ===== PART II ===== */}
          <h2
            style={{
              fontSize: '22px',
              fontWeight: 800,
              color: '#1E1B4B',
              margin: '48px 0 24px',
              paddingBottom: '12px',
              borderBottom: '3px solid #4F46E5',
            }}
          >
            Part II — Privacy Policy
          </h2>

          <div
            style={{
              background: '#EEF2FF',
              borderLeft: '4px solid #4F46E5',
              borderRadius: '0 12px 12px 0',
              padding: '14px 18px',
              marginBottom: '20px',
              fontSize: '15px',
              color: '#3730A3',
              lineHeight: '1.7',
            }}
          >
            This Privacy Policy explains how Aurora Prep LLC collects, uses, stores,
            and shares your personal information when you use the Services. Aurora
            Prep acts as the data controller for all personal data collected
            directly from users through the platform. We do not sell your personal
            information.
          </div>

          {/* 13 */}
          <SectionHeading>13. Information We Collect</SectionHeading>

          <Card title="13.1 Information You Provide Directly">
            <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
              <BulletItem>Account registration: name, email address</BulletItem>
              <BulletItem>Payment details: processed and stored by Stripe; Aurora Prep does not retain raw card data</BulletItem>
              <BulletItem>Profile information and preferences you choose to add</BulletItem>
              <BulletItem>Communications you send to Aurora Prep (e.g., support requests)</BulletItem>
            </ul>
          </Card>

          <Card title="13.2 Automatically Collected Information">
            <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
              <BulletItem>Usage data: pages visited, features used, session duration, referring URLs</BulletItem>
              <BulletItem>Device and technical data: IP address, browser type, operating system</BulletItem>
              <BulletItem>Cookies and similar tracking technologies (see Section 16)</BulletItem>
            </ul>
          </Card>

          <Card title="13.3 Assessment and Performance Data">
            <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
              <BulletItem>Practice case scores, timed assessment results, progress metrics</BulletItem>
              <BulletItem>Learning activity logs used to generate your personalized performance dashboard</BulletItem>
            </ul>
          </Card>

          {/* 14 */}
          <SectionHeading>14. How We Use Your Information</SectionHeading>
          <Para>Aurora Prep uses collected information for the following purposes:</Para>
          <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
            <BulletItem><strong>Provide Services</strong> — to provide, operate, and improve the Services</BulletItem>
            <BulletItem><strong>Personalization</strong> — to personalize your learning experience and generate performance dashboards</BulletItem>
            <BulletItem><strong>Payments</strong> — to process payments and manage your subscription</BulletItem>
            <BulletItem><strong>Transactional</strong> — to send transactional communications (receipts, account notices, password resets)</BulletItem>
            <BulletItem>
              <strong>Marketing</strong> — to send marketing and promotional emails; you may opt out at any time
              via the unsubscribe link or by contacting{' '}
              <Link href="mailto:support@auroraprep.com">support@auroraprep.com</Link>
            </BulletItem>
            <BulletItem><strong>Analytics</strong> — to analyze aggregated, anonymized usage patterns to improve platform quality</BulletItem>
            <BulletItem><strong>Security</strong> — to enforce these Terms and protect the security of the Services</BulletItem>
            <BulletItem><strong>Legal</strong> — to comply with applicable legal obligations</BulletItem>
          </ul>

          {/* 15 */}
          <SectionHeading>15. How We Share Your Information</SectionHeading>

          <Card title="Service Providers">
            <Para style={{ marginTop: '8px' }}>
              We share data with trusted third-party vendors who help us operate the
              Services (e.g., payment processing via Stripe, email delivery, hosting,
              analytics). These vendors process data only on our behalf and are bound
              by confidentiality obligations.
            </Para>
          </Card>

          <Card title="Sponsoring Institutions">
            <Para style={{ marginTop: '8px' }}>
              If your access is sponsored by a university or employer, we do not share
              individual user data with that institution. We may provide aggregate,
              anonymized usage reports only.
            </Para>
          </Card>

          <Card title="Legal Requirements">
            <Para style={{ marginTop: '8px' }}>
              We may disclose information if required by law, court order, or
              government authority, or to protect the rights, property, or safety of
              Aurora Prep, its users, or the public.
            </Para>
          </Card>

          <Card title="Business Transfers">
            <Para style={{ marginTop: '8px' }}>
              If Aurora Prep is acquired or merges with another entity, your
              information may be transferred as part of that transaction. We will
              notify you in advance of any such transfer.
            </Para>
          </Card>

          <div
            style={{
              background: '#EEF2FF',
              borderLeft: '4px solid #4F46E5',
              borderRadius: '0 12px 12px 0',
              padding: '14px 18px',
              marginBottom: '20px',
              fontSize: '15px',
              color: '#3730A3',
              fontWeight: 600,
              lineHeight: '1.7',
            }}
          >
            We do not sell your personal information. Aurora Prep does not sell or
            rent your personal data to third parties for their own marketing
            purposes.
          </div>

          {/* 16 */}
          <SectionHeading>16. Cookies and Tracking</SectionHeading>
          <Para>
            Aurora Prep uses cookies and similar technologies to operate the
            platform, remember your preferences, and analyze usage. We use the
            following categories of cookies:
          </Para>

          <Card title="Essential cookies">
            <Para style={{ marginTop: '8px' }}>
              Required for the platform to function (login sessions, security).
              These cannot be disabled.
            </Para>
          </Card>

          <Card title="Analytics cookies">
            <Para style={{ marginTop: '8px' }}>
              Tools such as Google Analytics help us understand how users navigate
              the platform. This data is aggregated and not used to identify
              individuals.
            </Para>
          </Card>

          <Card title="Marketing cookies">
            <Para style={{ marginTop: '8px' }}>
              If applicable, used to deliver relevant promotional content. You may
              opt out via your browser settings or by contacting us.
            </Para>
          </Card>

          <Para>
            You can control cookies through your browser settings. Disabling
            non-essential cookies will not affect your ability to access core
            Services features.
          </Para>

          {/* 17 */}
          <SectionHeading>17. Data Retention</SectionHeading>
          <Para>
            We retain your personal data for as long as your account is active or as
            needed to provide the Services. Specific retention periods:
          </Para>
          <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
            <BulletItem><strong>Account &amp; profile data:</strong> retained for the duration of your active subscription plus 2 years, or until you request deletion</BulletItem>
            <BulletItem><strong>Assessment &amp; performance data:</strong> retained for the duration of your account plus 2 years</BulletItem>
            <BulletItem><strong>Payment records:</strong> retained for 7 years in accordance with tax and financial recordkeeping requirements</BulletItem>
            <BulletItem>Upon verified deletion request, personal data will be removed from active systems within 30 days, except where retention is required by law</BulletItem>
          </ul>

          {/* 18 */}
          <SectionHeading>18. Data Security</SectionHeading>
          <Card>
            <Para style={{ marginTop: '8px' }}>
              Aurora Prep implements industry-standard technical and organizational
              measures to protect your personal information, including encrypted data
              transmission (TLS), access controls, and regular security reviews. In
              the event of a security breach affecting your personal data, we will
              notify affected users within 72 hours of becoming aware of the incident
              to the extent required by applicable law.
            </Para>
            <Para>
              No method of transmission over the internet or electronic storage is
              completely secure. While we take reasonable precautions, we cannot
              guarantee absolute security.
            </Para>
          </Card>

          {/* 19 */}
          <SectionHeading>19. Your Privacy Rights</SectionHeading>
          <Para>
            Depending on your location, you may have the following rights regarding
            your personal data:
          </Para>
          <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
            <BulletItem><strong>Access</strong> — request a copy of the personal data we hold about you</BulletItem>
            <BulletItem><strong>Correction</strong> — request that we correct inaccurate or incomplete data</BulletItem>
            <BulletItem><strong>Deletion</strong> — request that we delete your personal data, subject to legal retention requirements</BulletItem>
            <BulletItem><strong>Portability</strong> — request your data in a machine-readable format</BulletItem>
            <BulletItem><strong>Objection or Restriction</strong> — object to or request restriction of certain processing activities</BulletItem>
          </ul>
          <Para>
            To exercise any of these rights, contact us at{' '}
            <Link href="mailto:support@auroraprep.com">support@auroraprep.com</Link>.
            We will respond within 30 days. We may need to verify your identity
            before processing your request.
          </Para>

          {/* 20 */}
          <SectionHeading>20. California Residents (CCPA)</SectionHeading>
          <Para>
            If you are a California resident, you have additional rights under the
            California Consumer Privacy Act (CCPA):
          </Para>
          <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
            <BulletItem><strong>Right to Know</strong> — you may request disclosure of the categories and specific pieces of personal information we have collected about you</BulletItem>
            <BulletItem><strong>Right to Delete</strong> — you may request deletion of personal information we have collected, subject to certain exceptions</BulletItem>
            <BulletItem><strong>Right to Opt Out</strong> — Aurora Prep does not sell your personal information. There is nothing to opt out of regarding data sales.</BulletItem>
            <BulletItem><strong>Right to Non-Discrimination</strong> — we will not discriminate against you for exercising your CCPA rights</BulletItem>
          </ul>
          <Para>
            To submit a verifiable consumer request, contact us at{' '}
            <Link href="mailto:support@auroraprep.com">support@auroraprep.com</Link>
            {' '}with &ldquo;CCPA Request&rdquo; in the subject line.
          </Para>

          {/* 21 */}
          <SectionHeading>21. Children&rsquo;s Privacy</SectionHeading>
          <Para>
            The Services are not directed to individuals under the age of 16. Aurora
            Prep does not knowingly collect personal information from children under
            16. If we become aware that a user under 16 has provided personal
            information, we will promptly delete that information. If you believe a
            minor has provided us with personal data, please contact{' '}
            <Link href="mailto:support@auroraprep.com">support@auroraprep.com</Link>.
          </Para>

          {/* 22 */}
          <SectionHeading>22. Changes to This Privacy Policy</SectionHeading>
          <Para>
            We may update this Privacy Policy from time to time. We will notify you
            of material changes by email or by posting a notice on the platform and
            updating the &ldquo;Last Updated&rdquo; date at the top of this
            document. Your continued use of the Services after changes are posted
            constitutes acceptance of the revised Privacy Policy.
          </Para>

          {/* 23 */}
          <SectionHeading>23. Contact Us</SectionHeading>
          <Para>
            If you have questions, concerns, or requests related to this Privacy
            Policy or these Terms, please contact:
          </Para>
          <Card>
            <p style={{ fontSize: '15px', color: '#374151', lineHeight: '1.7' }}>
              <strong style={{ color: '#1E1B4B' }}>Aurora Prep LLC</strong><br />
              Email: <Link href="mailto:support@auroraprep.com">support@auroraprep.com</Link><br />
              Incorporated in: Delaware, United States
            </p>
          </Card>

          {/* Footer */}
          <footer style={{ textAlign: 'center', marginTop: '60px', fontSize: '13px', color: '#9CA3AF' }}>
            &copy; 2024–2026 Aurora Prep LLC. All rights reserved.
          </footer>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
